import { Cloud, Droplets, Sun, CloudRain, CloudSnow, CloudFog, Zap } from "lucide-react";

function iconFor(condition = "") {
    const c = condition.toLowerCase();
    if (c.includes("clear") || c.includes("sun")) return Sun;
    if (c.includes("rain") || c.includes("shower") || c.includes("drizzle")) return CloudRain;
    if (c.includes("snow")) return CloudSnow;
    if (c.includes("fog")) return CloudFog;
    if (c.includes("thunder")) return Zap;
    return Cloud;
}

function fmtDate(iso) {
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
    } catch {
        return iso;
    }
}

export default function WeatherCard({ card }) {
    const { city, country, period, expected, daily = [], source } = card;
    const HeadIcon = iconFor(expected?.dominant_condition);

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="weather-card"
        >
            {/* Gray dot top-right (no colored accent — weather is meta) */}
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full bg-[#A3A3A3] ring-4 ring-[#A3A3A3]/15"
                data-testid="card-color-dot"
            />

            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                        Expected weather
                    </div>
                    <h2 className="mt-1 text-[20px] font-semibold tracking-tight">
                        {city}
                        {country ? <span className="text-[#A3A3A3]"> · {country}</span> : null}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-[#737373]">
                        {fmtDate(period?.start)} → {fmtDate(period?.end)}
                    </p>
                </div>
                <HeadIcon size={28} strokeWidth={1.5} className="text-[#0A0A0A]" />
            </div>

            <div className="mt-5 flex items-end gap-3">
                <div className="text-[36px] font-semibold leading-none tracking-tight">
                    {expected?.avg_high_c != null ? `${Math.round(expected.avg_high_c)}°` : "—"}
                </div>
                <div className="pb-1 text-[16px] text-[#737373]">
                    /{" "}
                    {expected?.avg_low_c != null
                        ? `${Math.round(expected.avg_low_c)}°`
                        : "—"}
                </div>
                <div className="ml-3 pb-1 text-[14px] font-medium text-[#0A0A0A]">
                    {expected?.dominant_condition || "—"}
                </div>
            </div>
            <div className="mt-1 flex items-center gap-3 text-[12px] text-[#737373]">
                <span className="inline-flex items-center gap-1">
                    <Droplets size={11} strokeWidth={2} />
                    {expected?.avg_precip_mm != null
                        ? `${expected.avg_precip_mm} mm precip`
                        : "—"}
                </span>
                {expected?.avg_high_f != null && (
                    <span>
                        {Math.round(expected.avg_high_f)}°F / {Math.round(expected.avg_low_f)}°F
                    </span>
                )}
                <span className="rounded-full border border-[#E5E5E5] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#737373]">
                    {expected?.confidence || "—"} confidence
                </span>
            </div>

            {/* Daily strip */}
            {daily.length > 0 && (
                <div className="-mx-1 mt-5 overflow-x-auto pb-1">
                    <div className="flex gap-2 px-1" data-testid="weather-daily-strip">
                        {daily.map((d, i) => {
                            const Icon = iconFor(d.condition);
                            return (
                                <div
                                    key={i}
                                    className="flex min-w-[64px] flex-col items-center gap-1 rounded-lg border border-[#E5E5E5] bg-white px-2 py-2 text-center"
                                    data-testid={`weather-day-${i}`}
                                >
                                    <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                                        {fmtDate(d.date).split(",")[0]}
                                    </span>
                                    <Icon size={16} strokeWidth={1.75} className="text-[#0A0A0A]" />
                                    <span className="text-[12px] font-medium text-[#0A0A0A]">
                                        {d.high_c != null ? `${Math.round(d.high_c)}°` : "—"}
                                        <span className="text-[#A3A3A3]">
                                            /{d.low_c != null ? Math.round(d.low_c) : "—"}°
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <p className="mt-4 text-[11px] text-[#A3A3A3]">{source}</p>
        </article>
    );
}
